import * as React from "react";
import {Button, Checkbox, Header, Input, InputProps, List, Modal, SemanticShorthandItem} from 'semantic-ui-react';
import { v4 as uuid } from 'uuid';
import {authentication} from "../../authentication";

export interface ListCopyModalProps<T> {
    onClose: () => (void)
    initialItems?: T[]
    onSearch: (term: string, includeArchived: boolean) => Promise<T[]>
    open: boolean
    renderItem: (item: T) => JSX.Element
    icon?: any | SemanticShorthandItem<InputProps>
    placeholder?: string
    isSelectingItem?: boolean
    enableKeyboardNavigation?: boolean
}

interface ListCopyModalState<T> {
    open: boolean
    items: T[] | undefined
    isLoading: boolean
    selectedItem: T | null
    includeArchived: boolean
    query: string
    highlightedItemIndex: number
}

export class ListCopyModal<T> extends React.Component<ListCopyModalProps<T>, ListCopyModalState<T>> {
    constructor(props) {
        super(props)
        this.state = {
            open: false,
            items: this.props.initialItems,
            isLoading: false,
            selectedItem: null,
            includeArchived: false,
            query: "",
            highlightedItemIndex: this.props.initialItems != null && this.props.initialItems.length > 0 ? 0 : -1
        }
    }

    handleRef = (c) => {
        if (c) {
            c.focus()
        }
    }

    doSearch = async (value = this.state.query, includeArchived = this.state.includeArchived) => {

        this.setState({ isLoading: true })
        var result = await this.props.onSearch(value, includeArchived)
        this.setState({
            isLoading: false,
            items: result,
            query: value,
            highlightedItemIndex: result.length > 0 ? 0 : -1
        })

        console.log("Updating item list to ", result)
    }

    getItemIdOrThrow = (item: T): string => {
        const itemId = (item as {_id?: string})._id
        if (itemId == null || itemId.trim() === '') {
            throw new Error("Cannot copy ID: item does not provide a valid _id")
        }
        return itemId
    }

    onCopyClicked(item: T) {
        this.setState({ selectedItem: item })
        const itemId = this.getItemIdOrThrow(item)
        navigator.clipboard.writeText(itemId)
        this.handleClose()
    }

    getVisibleItems = (): T[] => {
        if (this.state.items == null || this.state.items.length === 0) {
            return []
        }
        return this.state.items.length > 15 ? this.state.items.slice(0, 15) : this.state.items
    }

    onInputKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
        if (this.state.isLoading || this.props.isSelectingItem) {
            return
        }

        const items = this.getVisibleItems()
        if (items.length === 0) {
            return
        }

        if (this.props.enableKeyboardNavigation && evt.key === 'ArrowDown') {
            evt.preventDefault()
            this.setState((prevState) => ({
                highlightedItemIndex: Math.min(prevState.highlightedItemIndex + 1, items.length - 1)
            }))
            return
        }

        if (this.props.enableKeyboardNavigation && evt.key === 'ArrowUp') {
            evt.preventDefault()
            this.setState((prevState) => ({
                highlightedItemIndex: Math.max(prevState.highlightedItemIndex - 1, 0)
            }))
            return
        }

        if (evt.key !== 'Enter') {
            return
        }

        const selectedIndex = this.props.enableKeyboardNavigation
            ? Math.max(0, Math.min(this.state.highlightedItemIndex, items.length - 1))
            : 0
        const selectedItem = items[selectedIndex]
        if (selectedItem != null) {
            evt.preventDefault()
            this.onCopyClicked(selectedItem)
        }
    }

    getItemStyle = (isHighlighted: boolean) => {
        if (!isHighlighted) {
            return {
                cursor: 'pointer',
                borderRadius: '6px',
                transition: 'background-color 120ms ease, box-shadow 120ms ease'
            }
        }
        return {
            cursor: 'pointer',
            borderRadius: '6px',
            paddingLeft: '10px',
            backgroundColor: '#e8f3ff',
            borderLeft: '4px solid #2185d0',
            boxShadow: 'inset 0 0 0 1px #b7d8fb',
            transition: 'background-color 120ms ease, box-shadow 120ms ease'
        }
    }

    renderListItems() {
        const items = this.getVisibleItems()

        if (items.length === 0) {
            return <List.Item key={0}>
                <List.Content>
                    Keine Ergebnisse
                </List.Content>
            </List.Item >
        }

        return items.map((x: T, index: number) => {
            const isHighlighted = this.props.enableKeyboardNavigation && index === this.state.highlightedItemIndex

            return <List.Item
                key={uuid()}
                style={this.getItemStyle(isHighlighted)}
                onClick={() => this.onCopyClicked(x)}
                onMouseEnter={() => {
                    if (this.props.enableKeyboardNavigation) {
                        this.setState({highlightedItemIndex: index})
                    }
                }}
            >
                <List.Content floated='right'>
                    <Button
                        onClick={(evt) => {
                            evt.stopPropagation()
                            this.onCopyClicked(x)
                        }}
                        loading={this.props.isSelectingItem && x == this.state.selectedItem}
                    >copy Id</Button>
                </List.Content>
                {this.props.renderItem(x)}
            </List.Item>

        })
    }

    handleClose = () => {
        this.setState({ items: null })
        this.props.onClose()
    }

    drawIncludeArchivedCheckbox() {
        return <>
            <Checkbox
                checked={this.state.includeArchived}
                onChange={() => {
                    this.doSearch(this.state.query, !this.state.includeArchived)
                    this.setState({includeArchived: !this.state.includeArchived})
                }}/> - include Archived
            <br/>
        </>
    }

    render() {

        const listItems = this.renderListItems()

        return <Modal dimmer="blurring" open={this.props.open}
            onClose={() => this.handleClose()} size="mini">
            <Modal.Content image>
                <Modal.Description>
                    <Header>{this.props.children}</Header>
                    {authentication.isSuperUser && this.drawIncludeArchivedCheckbox()}
                    <Input
                        loading={this.state.isLoading}
                        icon={this.props.icon || 'user'}
                        placeholder={this.props.placeholder || 'Suchen...'}
                        onChange={(evt) => this.doSearch(evt.target.value)}
                        onKeyDown={this.onInputKeyDown}
                        ref={this.handleRef}
                    />
                    <List divided verticalAlign='middle'>
                        {listItems}
                    </List>
                </Modal.Description>
            </Modal.Content>
        </Modal>
    }
}